import type {
  CanonicalTreeBranchConsumerResult,
} from '@/governor/tree/CanonicalTreeBranchConsumerAuthority';

import {
  verifyTreeGeometry,
  type TreeGeometryBounds,
  type TreeGeometryEdge,
  type TreeGeometryEvidenceIssue,
  type TreeGeometryEvidenceReport,
  type TreeGeometrySpouseLink,
} from '@/governor/treeGeometryEvidence';

import {
  enforceTreeLayoutRules,
} from '@/governor/tree/TreeLayoutRuleEnforcementAuthority';

import {
  LEGACY_ANCESTRY_HORIZONTAL_RULES,
  type LegacyAncestryCausalLocalization,
  type LegacyAncestryHorizontalRule,
} from './TreeRuntimeLegacyAncestryResidueCausalLocalization';

export const
TREE_RUNTIME_CANONICAL_BRANCH_COUNTERFACTUAL_SCHEMA =
  'CC-TREE-LIVE-003G3E3' as const;

const POSITIVE_CANONICAL_ROOTS =
  new Set<string>([
    'maternal',
    'paternal',
    'both',
    'self',
    'descendant',
    'partner-affiliated',
    'child-mediated',
    'affiliated',
  ]);

class CounterfactualReadonlyStringSet
  implements ReadonlySet<string>
{
  #values: Set<string>;

  constructor(
    values: ReadonlySet<string>,
  ) {
    this.#values =
      new Set(values);

    Object.freeze(this);
  }

  get size(): number {
    return this.#values.size;
  }

  has(value: string): boolean {
    return this.#values.has(value);
  }

  forEach(
    callback: (
      value: string,
      value2: string,
      set: ReadonlySet<string>,
    ) => void,
    thisArg?: unknown,
  ): void {
    for (const value of this.#values) {
      callback.call(
        thisArg,
        value,
        value,
        this,
      );
    }
  }

  entries() {
    return this.#values.entries();
  }

  keys() {
    return this.#values.keys();
  }

  values() {
    return this.#values.values();
  }

  [Symbol.iterator]() {
    return this.#values[Symbol.iterator]();
  }
}

class CounterfactualReadonlyStringMap
  implements ReadonlyMap<string, string>
{
  #values: Map<string, string>;

  constructor(
    values: ReadonlyMap<string, string>,
  ) {
    this.#values =
      new Map(values);

    Object.freeze(this);
  }

  get size(): number {
    return this.#values.size;
  }

  get(
    key: string,
  ): string | undefined {
    return this.#values.get(key);
  }

  has(
    key: string,
  ): boolean {
    return this.#values.has(key);
  }

  forEach(
    callback: (
      value: string,
      key: string,
      map: ReadonlyMap<string, string>,
    ) => void,
    thisArg?: unknown,
  ): void {
    for (const [key, value] of this.#values) {
      callback.call(
        thisArg,
        value,
        key,
        this,
      );
    }
  }

  entries() {
    return this.#values.entries();
  }

  keys() {
    return this.#values.keys();
  }

  values() {
    return this.#values.values();
  }

  [Symbol.iterator]() {
    return this.#values[Symbol.iterator]();
  }
}

const readonlyStringSet = (
  values: ReadonlySet<string>,
): ReadonlySet<string> =>
  new CounterfactualReadonlyStringSet(
    values,
  );

const readonlyStringMap = (
  values: ReadonlyMap<string, string>,
): ReadonlyMap<string, string> =>
  new CounterfactualReadonlyStringMap(
    values,
  );

export interface TreeRuntimeCanonicalBranchCounterfactualShadowPlan {
  readonly schema:
    typeof TREE_RUNTIME_CANONICAL_BRANCH_COUNTERFACTUAL_SCHEMA;

  readonly canonicalSemanticBranchPathByMemberId:
    ReadonlyMap<string, string>;

  readonly substitutedMemberIds:
    ReadonlySet<string>;

  readonly indeterminateMemberIds:
    ReadonlySet<string>;

  readonly canonicalMissingMemberIds:
    ReadonlySet<string>;

  readonly canonicalUnresolvedMemberIds:
    ReadonlySet<string>;

  readonly canonicalInvalidMemberIds:
    ReadonlySet<string>;

  readonly candidateMemberCount: number;
  readonly substitutedMemberCount: number;
  readonly indeterminateMemberCount: number;
  readonly canonicalMissingMemberCount: number;
  readonly canonicalUnresolvedMemberCount: number;
  readonly canonicalInvalidMemberCount: number;

  readonly productionSource:
    CanonicalTreeBranchConsumerResult['source'];

  readonly productionMigrationReadiness:
    CanonicalTreeBranchConsumerResult['migrationReadiness'];

  readonly productionAuthorityChanged: false;
  readonly sourceMutationAllowed: false;
  readonly legacyRetirementAuthorized: false;
  readonly readOnly: true;
  readonly repairExecuted: false;
  readonly privateIdentityPublished: false;
}

type CanonicalPathState =
  | 'POSITIVE'
  | 'UNRESOLVED'
  | 'INVALID';

const classifyCanonicalPath = (
  path: string,
): CanonicalPathState => {
  if (
    path.length === 0 ||
    path !== path.trim() ||
    /\s/.test(path)
  ) {
    return 'INVALID';
  }

  if (path === 'self') {
    return 'POSITIVE';
  }

  const segments =
    path.split('.');

  if (
    segments.some(
      segment =>
        segment.length === 0,
    )
  ) {
    return 'INVALID';
  }

  const root =
    segments[0] ?? '';

  const scope =
    segments[1] ?? '';

  const hasEvidenceSuffix =
    segments.length >= 3;

  if (root === 'unresolved') {
    return (
      scope === 'member' &&
      hasEvidenceSuffix
    )
      ? 'UNRESOLVED'
      : 'INVALID';
  }

  if (
    !POSITIVE_CANONICAL_ROOTS.has(
      root,
    )
  ) {
    return 'INVALID';
  }

  switch (root) {
    case 'maternal':
    case 'paternal':
    case 'both':
      return (
        scope === 'ancestor' &&
        hasEvidenceSuffix
      )
        ? 'POSITIVE'
        : 'INVALID';

    case 'descendant':
    case 'partner-affiliated':
    case 'child-mediated':
    case 'affiliated':
      return (
        scope === 'member' &&
        hasEvidenceSuffix
      )
        ? 'POSITIVE'
        : 'INVALID';

    case 'self':
    default:
      return 'INVALID';
  }
};

export const
createTreeRuntimeCanonicalBranchCounterfactualShadowPlan =
(
  branch: CanonicalTreeBranchConsumerResult,
): TreeRuntimeCanonicalBranchCounterfactualShadowPlan => {
  const candidates =
    new Set<string>([
      ...branch
        .legacyBranchPathByMemberId
        .keys(),
      ...branch
        .canonicalBranchPathByMemberId
        .keys(),
    ]);

  const canonicalPaths =
    new Map<string, string>();

  const substituted =
    new Set<string>();

  const indeterminate =
    new Set<string>();

  const missing =
    new Set<string>();

  const unresolved =
    new Set<string>();

  const invalid =
    new Set<string>();

  for (const memberId of candidates) {
    const canonicalPath =
      branch
        .canonicalBranchPathByMemberId
        .get(memberId);

    if (canonicalPath === undefined) {
      missing.add(memberId);
      indeterminate.add(memberId);
      continue;
    }

    switch (
      classifyCanonicalPath(canonicalPath)
    ) {
      case 'POSITIVE':
        canonicalPaths.set(
          memberId,
          canonicalPath,
        );
        substituted.add(memberId);
        break;

      case 'UNRESOLVED':
        unresolved.add(memberId);
        indeterminate.add(memberId);
        break;

      case 'INVALID':
      default:
        invalid.add(memberId);
        indeterminate.add(memberId);
        break;
    }
  }

  return Object.freeze({
    schema:
      TREE_RUNTIME_CANONICAL_BRANCH_COUNTERFACTUAL_SCHEMA,

    canonicalSemanticBranchPathByMemberId:
      readonlyStringMap(
        canonicalPaths,
      ),

    substitutedMemberIds:
      readonlyStringSet(
        substituted,
      ),

    indeterminateMemberIds:
      readonlyStringSet(
        indeterminate,
      ),

    canonicalMissingMemberIds:
      readonlyStringSet(
        missing,
      ),

    canonicalUnresolvedMemberIds:
      readonlyStringSet(
        unresolved,
      ),

    canonicalInvalidMemberIds:
      readonlyStringSet(
        invalid,
      ),

    candidateMemberCount:
      candidates.size,

    substitutedMemberCount:
      substituted.size,

    indeterminateMemberCount:
      indeterminate.size,

    canonicalMissingMemberCount:
      missing.size,

    canonicalUnresolvedMemberCount:
      unresolved.size,

    canonicalInvalidMemberCount:
      invalid.size,

    productionSource:
      branch.source,

    productionMigrationReadiness:
      branch.migrationReadiness,

    productionAuthorityChanged:
      false,

    sourceMutationAllowed:
      false,

    legacyRetirementAuthorized:
      false,

    readOnly:
      true,

    repairExecuted:
      false,

    privateIdentityPublished:
      false,
  });
};

const intersects = (
  left: ReadonlySet<string>,
  right: ReadonlySet<string>,
): boolean =>
  [...left].some(
    value => right.has(value),
  );

export const
isTreeRuntimeCanonicalBranchCounterfactualShadowPlanInternallyConsistent =
(
  plan:
    TreeRuntimeCanonicalBranchCounterfactualShadowPlan,
): boolean => {
  const indeterminateParts =
    plan.canonicalMissingMemberCount +
    plan.canonicalUnresolvedMemberCount +
    plan.canonicalInvalidMemberCount;

  return (
    plan.schema ===
      TREE_RUNTIME_CANONICAL_BRANCH_COUNTERFACTUAL_SCHEMA &&
    plan.candidateMemberCount ===
      plan.substitutedMemberCount +
        plan.indeterminateMemberCount &&
    plan.substitutedMemberCount ===
      plan.substitutedMemberIds.size &&
    plan.indeterminateMemberCount ===
      plan.indeterminateMemberIds.size &&
    plan.canonicalMissingMemberCount ===
      plan.canonicalMissingMemberIds.size &&
    plan.canonicalUnresolvedMemberCount ===
      plan.canonicalUnresolvedMemberIds.size &&
    plan.canonicalInvalidMemberCount ===
      plan.canonicalInvalidMemberIds.size &&
    plan.indeterminateMemberCount ===
      indeterminateParts &&
    plan.canonicalSemanticBranchPathByMemberId.size ===
      plan.substitutedMemberCount &&
    [...plan.substitutedMemberIds].every(
      memberId =>
        plan
          .canonicalSemanticBranchPathByMemberId
          .has(memberId),
    ) &&
    !intersects(
      plan.substitutedMemberIds,
      plan.indeterminateMemberIds,
    ) &&
    !intersects(
      plan.canonicalMissingMemberIds,
      plan.canonicalUnresolvedMemberIds,
    ) &&
    !intersects(
      plan.canonicalMissingMemberIds,
      plan.canonicalInvalidMemberIds,
    ) &&
    !intersects(
      plan.canonicalUnresolvedMemberIds,
      plan.canonicalInvalidMemberIds,
    ) &&
    plan.productionAuthorityChanged === false &&
    plan.sourceMutationAllowed === false &&
    plan.legacyRetirementAuthorized === false &&
    plan.readOnly === true &&
    plan.repairExecuted === false &&
    plan.privateIdentityPublished === false
  );
};

export const
isTreeRuntimeCanonicalBranchCounterfactualExecutionEligible =
(
  input: {
    readonly authenticatedSession:
      boolean;

    readonly plan:
      TreeRuntimeCanonicalBranchCounterfactualShadowPlan;

    readonly causalLocalization:
      LegacyAncestryCausalLocalization;
  },
): boolean => {
  const localization =
    input.causalLocalization;

  const branchDivergentFailureObserved =
    localization
      .rootBoundaryBranchDivergentCount >
      0 ||
    LEGACY_ANCESTRY_HORIZONTAL_RULES.some(
      rule =>
        localization
          .horizontal[rule]
          .branchDivergentCount >
        0,
    );

  return (
    input.authenticatedSession &&
    isTreeRuntimeCanonicalBranchCounterfactualShadowPlanInternallyConsistent(
      input.plan,
    ) &&
    input.plan.productionSource ===
      'legacy-fallback' &&
    input.plan.substitutedMemberCount >
      0 &&
    localization.evidenceStatus ===
      'OBSERVED' &&
    localization
      .branchLegacyFallbackSelected &&
    localization
      .layoutLegacyFallbackSelected &&
    localization.invalidJoinCount ===
      0 &&
    branchDivergentFailureObserved
  );
};


export interface TreeRuntimeCanonicalBranchCounterfactualLayout {
  readonly cards: readonly {
    readonly memberId: string | null;
    readonly x: number;
    readonly y: number;
    readonly tier: number;
    readonly isYou?: boolean;
    readonly needsConnection?: boolean;
  }[];

  readonly edges:
    readonly TreeGeometryEdge[];

  readonly spouseLinks:
    readonly TreeGeometrySpouseLink[];

  readonly bounds:
    TreeGeometryBounds;
}

export interface TreeRuntimeCanonicalBranchCounterfactualStructuralMember {
  readonly id: string;
  readonly parentIds?: readonly string[];
  readonly spouseId?: string | null;
}

export interface TreeRuntimeCanonicalBranchCounterfactualOptions {
  readonly cardWidth: number;
  readonly cardHeight: number;
  readonly rowGap: number;
  readonly tolerance?: number;
  readonly bezierSamples?: number;
}

export interface TreeRuntimeCanonicalBranchCounterfactualRuleCounts {
  readonly control: number;
  readonly resolved: number;
  readonly persisted: number;
  readonly introduced: number;
  readonly indeterminate: number;
}

export interface TreeRuntimeCanonicalBranchCounterfactualComparison {
  readonly schema:
    typeof TREE_RUNTIME_CANONICAL_BRANCH_COUNTERFACTUAL_SCHEMA;

  readonly availability:
    'OBSERVED' | 'INVALID_EVIDENCE';

  readonly controlParity: boolean;
  readonly controlWorldParity: boolean;
  readonly controlFailureParity: boolean;

  readonly controlFatalCount: number;
  readonly shadowFatalCount: number;

  readonly resolvedFatalCount: number;
  readonly persistedFatalCount: number;
  readonly introducedFatalCount: number;
  readonly indeterminateControlFatalCount: number;

  readonly rootBoundary:
    TreeRuntimeCanonicalBranchCounterfactualRuleCounts;

  readonly horizontal:
    Readonly<
      Record<
        LegacyAncestryHorizontalRule,
        TreeRuntimeCanonicalBranchCounterfactualRuleCounts
      >
    >;

  readonly horizontalControlCount: number;
  readonly horizontalResolvedCount: number;
  readonly horizontalPersistedCount: number;
  readonly horizontalIntroducedCount: number;
  readonly horizontalIndeterminateCount: number;

  readonly substitutedMemberCount: number;
  readonly indeterminateMemberCount: number;

  readonly invalidJoinCount: number;

  readonly sourceMutationAllowed: false;
  readonly causalAttributionAllowed: false;
  readonly legacyRetirementAuthorized: false;
  readonly productionAuthorityChanged: false;
  readonly privateIdentityPublished: false;
  readonly readOnly: true;
  readonly repairExecuted: false;
}

const buildShadowBranchPaths = (
  control:
    ReadonlyMap<string, string>,
  plan:
    TreeRuntimeCanonicalBranchCounterfactualShadowPlan,
): ReadonlyMap<string, string> => {
  const result =
    new Map(control);

  for (
    const [memberId, path]
    of plan.canonicalSemanticBranchPathByMemberId
  ) {
    result.set(
      memberId,
      path,
    );
  }

  return result;
};

export const buildTreeRuntimeCanonicalBranchCounterfactualGeometryEvidence =
(
  layout:
    TreeRuntimeCanonicalBranchCounterfactualLayout,

  structuralMembers:
    readonly TreeRuntimeCanonicalBranchCounterfactualStructuralMember[],

  branchPathByMemberId:
    ReadonlyMap<string, string>,

  viewerMemberId:
    string | null,

  centeredMemberId:
    string | null,

  options:
    TreeRuntimeCanonicalBranchCounterfactualOptions,
): TreeGeometryEvidenceReport => {
  const structuralById =
    new Map(
      structuralMembers.map(
        member =>
          [member.id, member] as const,
      ),
    );

  const base =
    verifyTreeGeometry(
      {
        cards:
          layout.cards.map(
            card => ({
              memberId:
                card.memberId,
              x:
                card.x,
              y:
                card.y,
              tier:
                card.tier,
              parentIds:
                card.memberId
                  ? [
                      ...(
                        structuralById
                          .get(card.memberId)
                          ?.parentIds ??
                        []
                      ),
                    ]
                  : [],
              isYou:
                card.isYou,
              needsConnection:
                card.needsConnection,
            }),
          ),

        edges:
          [...layout.edges],

        spouseLinks:
          [...layout.spouseLinks],

        companionChips:
          [],

        bounds:
          layout.bounds,

        requestedViewerMemberId:
          viewerMemberId,

        centeredMemberId,
      },
      options,
    );

  const ruleReport =
    enforceTreeLayoutRules(
      layout.cards.flatMap(
        card => {
          if (!card.memberId) {
            return [];
          }

          const structural =
            structuralById.get(
              card.memberId,
            );

          if (!structural) {
            return [];
          }

          return [{
            memberId:
              card.memberId,
            x:
              card.x,
            y:
              card.y,
            tier:
              card.tier,
            parentIds:
              [...(structural.parentIds ?? [])],
            branchPath:
              branchPathByMemberId
                .get(card.memberId) ??
              null,
            spouseId:
              structural.spouseId ??
              null,
            isYou:
              card.isYou,
          }];
        },
      ),
      {
        rowGap:
          options.rowGap,
        cardWidth:
          options.cardWidth,
        tolerance:
          options.tolerance,
      },
    );

  if (ruleReport.status === 'PASS') {
    return base;
  }

  const ruleIssues:
    TreeGeometryEvidenceIssue[] =
      ruleReport.violations.map(
        violation => ({
          status:
            'FAIL',
          rule:
            violation.rule,
          details:
            violation.details,
          memberIds:
            [...violation.memberIds],
        }),
      );

  const nonPassIssues =
    base.issues.filter(
      issue =>
        issue.rule !==
          'TREE-GEOMETRY-EVIDENCE',
    );

  return {
    ...base,
    status:
      'FAIL',
    issues:
      [
        ...nonPassIssues,
        ...ruleIssues,
      ],
    issueCount:
      nonPassIssues.length +
      ruleIssues.length,
  };
};

type PrivateFailureRecord = {
  readonly key: string;
  readonly issue:
    TreeGeometryEvidenceIssue;
  readonly indeterminate:
    boolean;
};

const privateIssueKey = (
  issue:
    TreeGeometryEvidenceIssue,
): string =>
  JSON.stringify([
    issue.rule,
    [...(issue.memberIds ?? [])].sort(),
    [...(issue.edgeKeys ?? [])].sort(),
    issue.routeRejectionStatus ?? null,
  ]);

const fatalRecords = (
  report:
    TreeGeometryEvidenceReport,

  layout:
    TreeRuntimeCanonicalBranchCounterfactualLayout,

  indeterminateMemberIds:
    ReadonlySet<string>,
): PrivateFailureRecord[] => {
  const edgeMembers =
    new Map<string, Set<string>>();

  for (const edge of layout.edges) {
    if (!edge.key) continue;

    edgeMembers.set(
      edge.key,
      new Set([
        ...edge.fromMemberIds,
        edge.toMemberId,
      ]),
    );
  }

  return report.issues
    .filter(
      issue =>
        issue.status ===
          'FAIL',
    )
    .map(
      issue => {
        const touched =
          new Set<string>(
            issue.memberIds ?? [],
          );

        for (
          const edgeKey
          of issue.edgeKeys ?? []
        ) {
          for (
            const memberId
            of edgeMembers.get(
              edgeKey,
            ) ?? []
          ) {
            touched.add(
              memberId,
            );
          }
        }

        return {
          key:
            privateIssueKey(
              issue,
            ),
          issue,
          indeterminate:
            [...touched].some(
              memberId =>
                indeterminateMemberIds
                  .has(memberId),
            ),
        };
      },
    );
};

const partitionFailures = (
  control:
    readonly PrivateFailureRecord[],
  shadow:
    readonly PrivateFailureRecord[],
) => {
  const used =
    new Array(
      shadow.length,
    ).fill(false);

  const resolved:
    PrivateFailureRecord[] = [];

  const persisted:
    PrivateFailureRecord[] = [];

  for (const record of control) {
    const index =
      shadow.findIndex(
        (candidate, i) =>
          !used[i] &&
          candidate.key ===
            record.key,
      );

    if (index < 0) {
      resolved.push(record);
      continue;
    }

    used[index] = true;
    persisted.push(record);
  }

  const introduced =
    shadow.filter(
      (_, index) =>
        !used[index],
    );

  return {
    resolved,
    persisted,
    introduced,
  };
};

const rootBoundaryFailure = (
  record:
    PrivateFailureRecord,
): boolean =>
  record.issue.rule ===
    'GEOMETRY-CONNECTOR-ROUTE-REJECTED' &&
  record.issue.routeRejectionStatus ===
    'REJECTED-ROOT-BOUNDARY';

const horizontalRuleOf = (
  record:
    PrivateFailureRecord,
): LegacyAncestryHorizontalRule | null =>
  LEGACY_ANCESTRY_HORIZONTAL_RULES
    .includes(
      record.issue.rule as
        LegacyAncestryHorizontalRule,
    )
      ? record.issue.rule as
          LegacyAncestryHorizontalRule
      : null;

const countsFor = (
  control:
    readonly PrivateFailureRecord[],
  resolved:
    readonly PrivateFailureRecord[],
  persisted:
    readonly PrivateFailureRecord[],
  introduced:
    readonly PrivateFailureRecord[],
  predicate:
    (record: PrivateFailureRecord) => boolean,
): TreeRuntimeCanonicalBranchCounterfactualRuleCounts =>
  Object.freeze({
    control:
      control.filter(
        predicate,
      ).length,

    resolved:
      resolved.filter(
        predicate,
      ).length,

    persisted:
      persisted.filter(
        predicate,
      ).length,

    introduced:
      introduced.filter(
        predicate,
      ).length,

    indeterminate:
      control.filter(
        record =>
          predicate(record) &&
          record.indeterminate,
      ).length,
  });

export const compareTreeRuntimeCanonicalBranchCounterfactualShadow =
(
  input: {
    readonly controlLayout:
      TreeRuntimeCanonicalBranchCounterfactualLayout;

    readonly shadowLayout:
      TreeRuntimeCanonicalBranchCounterfactualLayout;

    readonly structuralMembers:
      readonly TreeRuntimeCanonicalBranchCounterfactualStructuralMember[];

    readonly controlBranchPathByMemberId:
      ReadonlyMap<string, string>;

    readonly shadowPlan:
      TreeRuntimeCanonicalBranchCounterfactualShadowPlan;

    readonly productionGeometryEvidence:
      TreeGeometryEvidenceReport;

    readonly viewerMemberId:
      string | null;

    readonly centeredMemberId:
      string | null;

    readonly options:
      TreeRuntimeCanonicalBranchCounterfactualOptions;
  },
): TreeRuntimeCanonicalBranchCounterfactualComparison => {
  const controlEvidence =
    buildTreeRuntimeCanonicalBranchCounterfactualGeometryEvidence(
      input.controlLayout,
      input.structuralMembers,
      input.controlBranchPathByMemberId,
      input.viewerMemberId,
      input.centeredMemberId,
      input.options,
    );

  const shadowBranchPaths =
    buildShadowBranchPaths(
      input.controlBranchPathByMemberId,
      input.shadowPlan,
    );

  const shadowEvidence =
    buildTreeRuntimeCanonicalBranchCounterfactualGeometryEvidence(
      input.shadowLayout,
      input.structuralMembers,
      shadowBranchPaths,
      input.viewerMemberId,
      input.centeredMemberId,
      input.options,
    );

  const control =
    fatalRecords(
      controlEvidence,
      input.controlLayout,
      input.shadowPlan
        .indeterminateMemberIds,
    );

  const shadow =
    fatalRecords(
      shadowEvidence,
      input.shadowLayout,
      input.shadowPlan
        .indeterminateMemberIds,
    );

  const productionFatalKeys =
    input.productionGeometryEvidence
      .issues
      .filter(
        issue =>
          issue.status ===
            'FAIL',
      )
      .map(
        privateIssueKey,
      )
      .sort();

  const controlFatalKeys =
    control
      .map(
        record =>
          record.key,
      )
      .sort();

  const controlFailureParity =
    JSON.stringify(
      productionFatalKeys,
    ) ===
    JSON.stringify(
      controlFatalKeys,
    );

  const controlWorldParity =
    input.productionGeometryEvidence
      .cardCount ===
        controlEvidence.cardCount &&
    input.productionGeometryEvidence
      .edgeCount ===
        controlEvidence.edgeCount &&
    input.productionGeometryEvidence
      .spouseLinkCount ===
        controlEvidence.spouseLinkCount &&
    input.productionGeometryEvidence
      .companionChipCount ===
        0;

  const controlParity =
    controlFailureParity &&
    controlWorldParity;

  const partition =
    partitionFailures(
      control,
      shadow,
    );

  const rootBoundary =
    countsFor(
      control,
      partition.resolved,
      partition.persisted,
      partition.introduced,
      rootBoundaryFailure,
    );

  const horizontal =
    Object.fromEntries(
      LEGACY_ANCESTRY_HORIZONTAL_RULES.map(
        rule => [
          rule,
          countsFor(
            control,
            partition.resolved,
            partition.persisted,
            partition.introduced,
            record =>
              horizontalRuleOf(
                record,
              ) === rule,
          ),
        ],
      ),
    ) as Record<
      LegacyAncestryHorizontalRule,
      TreeRuntimeCanonicalBranchCounterfactualRuleCounts
    >;

  const horizontalControlCount =
    Object.values(horizontal)
      .reduce(
        (sum, value) =>
          sum + value.control,
        0,
      );

  const horizontalResolvedCount =
    Object.values(horizontal)
      .reduce(
        (sum, value) =>
          sum + value.resolved,
        0,
      );

  const horizontalPersistedCount =
    Object.values(horizontal)
      .reduce(
        (sum, value) =>
          sum + value.persisted,
        0,
      );

  const horizontalIntroducedCount =
    Object.values(horizontal)
      .reduce(
        (sum, value) =>
          sum + value.introduced,
        0,
      );

  const horizontalIndeterminateCount =
    Object.values(horizontal)
      .reduce(
        (sum, value) =>
          sum + value.indeterminate,
        0,
      );

  const arithmeticValid =
    partition.resolved.length +
      partition.persisted.length ===
        control.length &&
    partition.persisted.length +
      partition.introduced.length ===
        shadow.length;

  const invalidJoinCount =
    arithmeticValid
      ? 0
      : 1;

  return Object.freeze({
    schema:
      TREE_RUNTIME_CANONICAL_BRANCH_COUNTERFACTUAL_SCHEMA,

    availability:
      controlParity &&
      arithmeticValid
        ? 'OBSERVED'
        : 'INVALID_EVIDENCE',

    controlParity,
    controlWorldParity,
    controlFailureParity,

    controlFatalCount:
      control.length,

    shadowFatalCount:
      shadow.length,

    resolvedFatalCount:
      partition.resolved.length,

    persistedFatalCount:
      partition.persisted.length,

    introducedFatalCount:
      partition.introduced.length,

    indeterminateControlFatalCount:
      control.filter(
        record =>
          record.indeterminate,
      ).length,

    rootBoundary:
      Object.freeze(
        rootBoundary,
      ),

    horizontal:
      Object.freeze({
        ...horizontal,
      }),

    horizontalControlCount,
    horizontalResolvedCount,
    horizontalPersistedCount,
    horizontalIntroducedCount,
    horizontalIndeterminateCount,

    substitutedMemberCount:
      input.shadowPlan
        .substitutedMemberCount,

    indeterminateMemberCount:
      input.shadowPlan
        .indeterminateMemberCount,

    invalidJoinCount,

    sourceMutationAllowed:
      false,

    causalAttributionAllowed:
      false,

    legacyRetirementAuthorized:
      false,

    productionAuthorityChanged:
      false,

    privateIdentityPublished:
      false,

    readOnly:
      true,

    repairExecuted:
      false,
  });
};
